import { Component, OnInit, Input, OnDestroy, ElementRef, ViewChild, ViewChildren, QueryList } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { PowerpointService } from 'src/app/powerpoint.service';
import { PlanitService } from 'src/app/input/planit/planit.service';
import { TableConfigurations, PowerPointTableData } from 'src/app/types/powerpoint.types';
import { takeWhile } from 'rxjs/operators';
import { ExportService } from 'src/app/export.service';
import { MatAccordion } from '@angular/material/expansion';
import { AlertService } from '@shared/services/alert.service';
import { Router } from '@angular/router';
import { ChatService } from '../../chat.service';

@Component({
    selector: 'app-planit-output',
    templateUrl: './planit-output.component.html',
    styleUrls: ['./planit-output.component.css'],
})
export class PlanitOutputComponent implements OnInit, OnDestroy {
    @Input() response: string = '';
    @Input() subscription_id: string = '';
    @ViewChild(MatAccordion) accordion: MatAccordion;
    @ViewChildren('tableElement') tableElements: QueryList<ElementRef>;
    showResponse = true;
    showError = false;
    isLoading = false;
    waitingMessage = false;
    showDiscrapancy = false;
    isDestroyed = false;
    safeHtml: SafeHtml;
    agendaExample = {
        '1': [
            {
                time: '09:00 - 10:00',
                title: 'Introduction',
                activities: ['Welcome and introductions', 'Overview of the workshop', 'Objectives and outcomes'],
            },
            {
                time: '10:00 - 10:15',
                title: 'Coffee break',
                activities: [],
            },
            {
                time: '10:15 - 11:30',
                title: 'Sustainability in business',
                activities: ['What is sustainability?', 'Why is sustainability important for businesses?', 'The challenges of sustainability'],
            },
            {
                time: '11:30 - 12:00',
                title: 'Discussion',
                activities: ['Open discussion on sustainability in business'],
            },
        ],
        '2': [
            {
                time: '09:00 - 10:00',
                title: 'Strategies for sustainability',
                activities: ['Strategies for sustainability', 'Case studies of sustainable businesses', 'How to measure sustainability'],
            },
            {
                time: '10:00 - 10:15',
                title: 'Coffee break',
                activities: [],
            },
            {
                time: '10:15 - 11:30',
                title: 'Sustainability in practice',
                activities: ['How to implement sustainability in your business', 'Tips and tricks for sustainability', 'Challenges of implementing sustainability'],
            },
            {
                time: '11:30 - 12:00',
                title: 'Conclusion',
                activities: ['Summary of the workshop', 'Next steps'],
            },
        ],
    };
    temp = 'Agile software development with focus on Scrum based on the most recent Scrum guide. The audience is relatively new to the topic and will need to start from the foundations.';
    agendaTasks = null;
    constructor(
        private sanitizer: DomSanitizer,
        private powerpointService: PowerpointService,
        private planitService: PlanitService,
        private alertService: AlertService,
        private exportService: ExportService,
    ) {}

    ngOnInit(): void {
        // this.sanitize();
        this.planitService.planitResponse.pipe(takeWhile(() => !this.isDestroyed)).subscribe((response) => {
            console.log(response);
            this.agendaTasks = response;
        });
    }

    ngOnChanges(): void {
        if (this.response) {
            this.showError = false;
            this.showDiscrapancy = false;
            console.log(this.response);
            if (this.response.indexOf('```') !== -1) {
                this.response = this.response.substring(this.response.indexOf('```') + 3, this.response.lastIndexOf('```'));
            } else {
                this.response = this.response.substring(this.response.indexOf('{'), this.response.lastIndexOf('}') + 1);
            }
            if (this.response.toLowerCase().startsWith('json')) {
                this.response = this.response.substring(4);
            }
            console.log(this.response);
            try {
                this.agendaTasks = JSON.parse(this.response);
            } catch (error) {
                console.error(error);
                this.showError = true;
            }

            this.showResponse = true;
        }
    }

    getSlideContent(): PowerPointTableData[] {
        const keys = Object.keys(this.agendaTasks);
        return keys.map((key, idx) => {
            return {
                title: `Agenda for Day ${idx + 1}`,
                subtitle: '',
                data: this.agendaTasks[key],
            };
        });
    }

    generatePowerPoint() {
        const slideContent = this.getSlideContent();
        const config: TableConfigurations = {
            headers: ['Time', 'Title', 'Activities'],
            columnWidth: [1.5, 3, 5],
            options: {
                time: { valign: 'middle', align: 'center' },
                title: { valign: 'middle' },
                activities: { bullet: true, h: 2 },
            },
            headerOptions: {
                time: { align: 'center' },
                title: {},
                activities: {},
            },
            maxTableRowsPerSlide: 5,
        };
        this.powerpointService.generateSlides(slideContent, config, 'agenda_planit.pptx');
    }

    get agendaKeys() {
        if (this.agendaTasks) {
            return Object.keys(this.agendaTasks);
        }
        return [];
    }

    calcStartAndEndDates(data): string {
        if (data && data.length > 0) {
            let startDate = data[0].time;
            let endDate = data[data.length - 1].time;

            if (startDate[startDate.indexOf('-') - 1] === ' ') {
                startDate = startDate.substring(0, startDate.indexOf('-') - 1);
            } else {
                startDate = startDate.substring(0, startDate.indexOf('-'));
            }

            if (endDate[endDate.indexOf('-') + 1] === ' ') {
                endDate = endDate.substring(endDate.indexOf('-') + 1);
            } else {
                endDate = endDate.substring(endDate.indexOf('-'));
            }
            if (!this.planitService.endTime.includes(endDate.substring(0, endDate.indexOf(':')).trim())) {
                this.showDiscrapancy = true;
                endDate += ' *';
            }
            return this.calcTime(startDate + ' - ' + endDate);
        }
        return '';
    }

    calcTime(time: string): string {
        let startTime = time;
        let endTime = time;

        if (startTime[startTime.indexOf('-') - 1] === ' ') {
            startTime = startTime.substring(0, startTime.indexOf('-') - 1);
        } else {
            startTime = startTime.substring(0, startTime.indexOf('-'));
        }
        if (endTime[endTime.indexOf('-') + 1] === ' ') {
            endTime = endTime.substring(endTime.indexOf('-') + 1);
        } else {
            endTime = endTime.substring(endTime.indexOf('-'));
        }
        if (parseInt(endTime.substring(0, endTime.indexOf(':')).trim()) < 10) {
            endTime = String(parseInt(endTime.substring(0, endTime.indexOf(':')).trim()) + 12) + ':00';
        }
        return startTime + ' - ' + endTime;
    }

    exportAsPdf() {
        let opt = {
            margin: 1,
            filename: 'agenda.pdf',
            image: { type: 'jpeg', quality: 1 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'cm', format: 'letter', orientation: 'portrait' },
        };

        this.exportService.exportAsPdf('downloadableContent', opt);
    }

    copyTableAsHtml() {
        const tablesHtml: string[] = [];

        this.tableElements.forEach((tableElement: ElementRef, index: number) => {
            const agendaHtml = `<h3>Agenda for day ${index + 1}</h3>`;
            const tableHtml: string = tableElement.nativeElement.outerHTML;
            tablesHtml.push(agendaHtml + tableHtml);
        });

        const combinedHtml: string = tablesHtml.join('<br>');
        this.copyTextToClipboard(combinedHtml);
    }

    copyToClipboard(): void {
        const formattedCSV = this.generateCSV();
        this.copyTextToClipboard(formattedCSV);
    }

    generateCSV(): string {
        let csv = 'id;time;title;activities\n';

        for (const key in this.agendaTasks) {
            this.agendaTasks[key].forEach((item) => {
                const id = key;
                const time = item.time;
                const title = item.title;
                const activities = item.activities.join(';');

                csv += `${id};${time};${title};${activities}\n`;
            });
        }

        return csv;
    }

    async copyTextToClipboard(text: string) {
        if (text) {
            const tableHtml: string = text;
            const blobHtml = new Blob([tableHtml], { type: 'text/html' });

            try {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': blobHtml,
                    }),
                ]);
                this.alertService.openSnackBar('Copied to clipboard successfully!');
            } catch (error) {
                console.error('Failed to copy text: ', error);
            }
        }

        /* navigator.clipboard.writeText(text)
      .then(() => {
        this.alertService.openSnackBar("Copied to clipboard successfully!");
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
      }); */
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }
}
